import {zIndex, tokens} from '@commercial-helios/ui/theme'
import {Script} from 'gatsby'
import React from 'react'
import styled from 'styled-components'

import PlayButton from '@/components/play-button'
import {Scripts} from '@/utils/external-scripts'
import RichText, {hasRichText} from '@helios/shared/components/rich-text'
import {Video} from '@helios/shared/components/video'
import {Section} from '@helios/shared/styles/grid'
import {Text} from '@helios/shared/styles/typography'
import type InlineVideo from '@helios/shared/types/inline-video'
import mq from '@helios/shared/utils/media-query'

interface Props {
    preTitle: string
    title: string
    body: string
    videoId: string
    thumbnailVideo: InlineVideo
    dataTestId?: string
}

export enum VideoState {
    Initial = 'Initial',
    Open = 'Open',
    Closed = 'Closed',
}

const events: Wistia.PlayerEvent[] = ['play', 'pause', 'end']

const MAX_HEIGHT = 1000

export const VideoPopoverWithText = ({
    preTitle,
    title,
    body,
    thumbnailVideo,
    videoId,
    dataTestId,
}: Props) => {
    const transformingVideoContainerRef = React.useRef<HTMLDivElement | null>(null)
    const videoRef = React.useRef<HTMLDivElement | null>(null)

    const [videoState, setVideoState] = React.useState<VideoState>(VideoState.Initial)

    const isVideoPopoverInitial = videoState === VideoState.Initial
    const isVideoPopoverOpen = videoState === VideoState.Open
    const isVideoPopoverClosed = videoState === VideoState.Closed

    const [video, setVideo] = React.useState<Wistia.Video | null>(null)

    const [offset, setOffset] = React.useState(0)

    const openPopOver = () => {
        if (transformingVideoContainerRef?.current) {
            setOffset(transformingVideoContainerRef.current.getBoundingClientRect().top)
        }
        setVideoState(VideoState.Open)
        document.body.style.overflow = 'hidden'
    }

    const closePopover = React.useCallback(() => {
        setVideoState(VideoState.Closed)
        setOffset(0)
        video?.pause()
        document.body.style.overflow = 'auto'
    }, [video])

    React.useEffect(() => {
        const keyUpHandler = (event: KeyboardEvent) => {
            if (event.code === 'Escape' && isVideoPopoverOpen) {
                event.preventDefault()
                closePopover()
            }
        }

        window.addEventListener('keyup', keyUpHandler)
        return () => {
            window.removeEventListener('keyup', keyUpHandler)
        }
    }, [isVideoPopoverOpen, closePopover])

    const embedInitConfig = React.useMemo(
        () =>
            ({
                id: videoId,
                onEmbedded: (video) => {
                    setVideo(video)
                    events.forEach((event) => {
                        if (isVideoPopoverOpen) {
                            video.play()
                            video.releaseChromeless()
                        } else {
                            video.requestChromeless()
                        }

                        video.bind(event, () => {
                            if (video.state() === 'ended') {
                                closePopover()
                            }
                        })
                    })
                },
            } as Wistia.Item),
        [videoId, isVideoPopoverOpen, closePopover]
    )

    React.useEffect(() => {
        window._wq = window._wq || []
        window._wq.push(embedInitConfig)

        return () => {
            window._wq.push({revoke: embedInitConfig})
        }
    }, [embedInitConfig])

    const videoClickHandler = () => isVideoPopoverClosed && openPopOver()

    return (
        <>
            <Script
                src={Scripts.WISTIA}
                onError={() => console.error(`Wistia script failed to load.`)}
            />
            <Section data-testid={dataTestId}>
                <Wrapper>
                    <HeaderWrapper>
                        <TitleWrapper>
                            <Text variant="paragraph-large" color="brand">
                                {preTitle}
                            </Text>

                            {hasRichText(title) && (
                                <RichText as="h2" variant="heading-large" content={title} />
                            )}
                        </TitleWrapper>

                        <Text color="muted">{body}</Text>
                    </HeaderWrapper>

                    <TransformingVideoContainer
                        $show={isVideoPopoverOpen}
                        ref={transformingVideoContainerRef}
                    >
                        <Backdrop $show={isVideoPopoverOpen} onClick={closePopover} />

                        <ActualVideo ref={videoRef} $videoState={videoState} $offset={offset}>
                            <WistiaVideoContainer $maxHeight={MAX_HEIGHT}>
                                <ResumeVideoButton
                                    onClick={videoClickHandler}
                                    $videoState={videoState}
                                />
                                <WistiaWrapper
                                    $show={isVideoPopoverOpen}
                                    className={`
                            wistia_embed wistia_async_${videoId}
                            videoFoam['maxHeight']=${MAX_HEIGHT}
                            playButton=false
                            controlsVisibleOnLoad=false
                            wmode=transparent
                            `}
                                />
                            </WistiaVideoContainer>
                        </ActualVideo>

                        <Thumbnail
                            $maxHeight={MAX_HEIGHT}
                            $show={isVideoPopoverInitial}
                            $offset={-offset}
                        >
                            <ThumbnailVideo video={thumbnailVideo} />
                            <LaunchPopoverButton
                                $show={isVideoPopoverInitial}
                                onClick={openPopOver}
                            />
                        </Thumbnail>
                    </TransformingVideoContainer>
                </Wrapper>
            </Section>
        </>
    )
}

type ActualVideoProps = {
    $offset: number
    $videoState: VideoState
}

type ThumbnailProps = {
    $show: boolean
    $offset: number
    $maxHeight: number
}

const Wrapper = styled.div`
    display: grid;
    gap: ${tokens.spacing20};

    --maxWidth: 540px;

    ${mq.min('sm')} {
        gap: ${tokens.spacing40};
    }
`

const TitleWrapper = styled.div`
    display: grid;
    gap: ${tokens.spacing12};
    max-width: var(--maxWidth);
    align-self: start;
`

const HeaderWrapper = styled.div`
    display: grid;
    gap: ${tokens.spacing20};

    ${mq.min('sm')} {
        grid-auto-flow: column;
        gap: ${tokens.spacing32};
    }
`

const TransformingVideoContainer = styled.div<{$show: boolean}>`
    position: relative;
    isolation: isolate;
    z-index: ${(props) => (props.$show ? zIndex.videoPopover : 0)};
`

const Backdrop = styled.div<{$show: boolean}>`
    position: fixed;
    top: 0;
    left: 0;
    z-index: 2;
    width: 100vw;
    height: 100vh;
    pointer-events: ${(props) => (props.$show ? 'auto' : 'none')};
    background: ${tokens.shade900lighten80};
    opacity: ${(props) => (props.$show ? 1 : 0)};
    transition: opacity ${tokens.slowOut};
`

const ActualVideo = styled.div<ActualVideoProps>`
    position: absolute;
    inset: 0;
    z-index: ${(props) => (props.$videoState !== VideoState.Initial ? 3 : 1)};
    opacity: ${(props) => (props.$videoState !== VideoState.Initial ? 1 : 0)};
    transform: ${(props) =>
        props.$videoState === VideoState.Open
            ? `translatey(calc((100vh - 100%) / 2 - 1px * ${props.$offset})) `
            : 'translateY(0)'};

    @media (prefers-reduced-motion: no-preference) {
        transition: transform ${tokens.slowOut};
        will-change: transform;
    }
`

const WistiaVideoContainer = styled.div<{$maxHeight: number}>`
    position: relative;
    padding-bottom: ${(props) => `min(${(9 / 16) * 100}%, ${props.$maxHeight}px) `};
    isolation: isolate;
`

const LaunchPopoverButton = styled(PlayButton)<{$show: boolean}>`
    position: absolute;
    top: 50%;
    left: 50%;
    display: ${(props) => (props.$show ? 'block' : 'none')};
    transform: translate(-50%, -50%);
`

const ResumeVideoButton = styled(PlayButton)<{$videoState: VideoState}>`
    position: absolute;
    top: 50%;
    left: 50%;
    z-index: ${(props) => (props.$videoState === VideoState.Closed ? '2' : '0')};
    transform: translate(-50%, -50%);
`

const WistiaWrapper = styled.div<{$show: boolean}>`
    position: absolute;
    margin: 0 auto;
    pointer-events: ${(props) => (props.$show ? 'auto' : 'none')};
    inset: 0;
`

const Thumbnail = styled.div<ThumbnailProps>`
    position: relative;
    padding-bottom: ${(props) => `min(${(9 / 16) * 100}%, ${props.$maxHeight}px) `};
    z-index: 2;
    pointer-events: ${(props) => (props.$show ? 'auto' : 'none')};
    opacity: ${(props) => (props.$show ? 1 : 0)};
    transform: ${(props) => (props.$show ? 'translate(0)' : `translatey(${props.$offset}px) `)};

    @media (prefers-reduced-motion: no-preference) {
        transition: transform ${tokens.slowOut};
        will-change: transform;
    }
`

const ThumbnailVideo = styled(Video)`
    position: absolute;
    inset: 0;
`
